import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatDateTime } from "@/utils/dateFormat";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, Megaphone, AlertTriangle, Bell, Eye, EyeOff } from "lucide-react";

const API = '/api';

export default function Announcements() {
  const { isAdmin, getAuthHeader } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [formData, setFormData] = useState({
    event_id: "",
    title: "",
    message: "",
    priority: "normal",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [announcementsRes, eventsRes] = await Promise.all([
        axios.get(`${API}/announcements?active_only=false`),
        axios.get(`${API}/events`),
      ]);
      setAnnouncements(announcementsRes.data);
      setEvents(eventsRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        event_id: formData.event_id || null,
        title: formData.title,
        message: formData.message,
        priority: formData.priority,
      };

      if (editingAnnouncement) {
        await axios.put(`${API}/announcements/${editingAnnouncement.id}`, payload, {
          headers: getAuthHeader()
        });
        toast.success("Announcement updated");
      } else {
        await axios.post(`${API}/announcements`, payload, {
          headers: getAuthHeader()
        });
        toast.success("Announcement created");
      }

      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save announcement");
    }
  };

  const handleToggle = async (id) => {
    try {
      await axios.patch(`${API}/announcements/${id}/toggle`, {}, {
        headers: getAuthHeader()
      });
      fetchData();
    } catch (error) {
      toast.error("Failed to toggle announcement");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    try {
      await axios.delete(`${API}/announcements/${id}`, {
        headers: getAuthHeader()
      });
      toast.success("Announcement deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete announcement");
    }
  };

  const openEditDialog = (announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      event_id: announcement.event_id || "",
      title: announcement.title,
      message: announcement.message,
      priority: announcement.priority,
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingAnnouncement(null);
    setFormData({
      event_id: "",
      title: "",
      message: "",
      priority: "normal",
    });
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case "urgent":
        return <Badge className="bg-red-100 text-red-800"><AlertTriangle size={12} className="mr-1" /> Urgent</Badge>;
      case "high":
        return <Badge className="bg-orange-100 text-orange-800"><Bell size={12} className="mr-1" /> High</Badge>;
      default:
        return <Badge className="bg-blue-100 text-blue-800">Normal</Badge>;
    }
  };

  const getEventName = (eventId) => {
    if (!eventId) return "All Events (Global)";
    const event = events.find(e => e.id === eventId);
    return event?.name || "Unknown Event";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }

  return (
    <div data-testid="announcements-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl lg:text-4xl font-bold text-slate-800 tracking-tight">
            Announcements
          </h1>
          <p className="text-slate-500 mt-1">Event announcements and notices</p>
        </div>

        {isAdmin() && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={() => { resetForm(); setDialogOpen(true); }}
                className="bg-[#1B3B6F] hover:bg-[#1B3B6F]/90"
                data-testid="create-announcement-btn"
              >
                <Plus size={20} className="mr-2" />
                New Announcement
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle className="font-serif text-xl">
                  {editingAnnouncement ? "Edit Announcement" : "New Announcement"}
                </DialogTitle>
                <DialogDescription>
                  Post an announcement for shooters
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="event">Event (optional)</Label>
                  <Select
                    value={formData.event_id || "global"}
                    onValueChange={(val) => setFormData({ ...formData, event_id: val === "global" ? "" : val })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Global (all events)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="global">Global (all events)</SelectItem>
                      {events.map((event) => (
                        <SelectItem key={event.id} value={event.id}>
                          {event.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Announcement title"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="message">Message</Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Announcement details..."
                    rows={4}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="priority">Priority</Label>
                  <Select
                    value={formData.priority}
                    onValueChange={(val) => setFormData({ ...formData, priority: val })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button type="submit" className="w-full bg-[#1B3B6F] hover:bg-[#1B3B6F]/90">
                  {editingAnnouncement ? "Update" : "Post Announcement"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Announcements List */}
      {announcements.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Megaphone className="mx-auto text-slate-300 mb-4" size={48} />
            <p className="text-slate-500">No announcements yet.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {announcements.map((announcement) => (
            <Card 
              key={announcement.id} 
              className={`${!announcement.active ? "opacity-50" : ""} ${
                announcement.priority === "urgent" ? "border-red-300 bg-red-50/50" :
                announcement.priority === "high" ? "border-orange-300 bg-orange-50/50" : ""
              }`}
              data-testid={`announcement-${announcement.id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <h3 className="font-serif text-xl font-semibold text-slate-800">
                        {announcement.title}
                      </h3>
                      {getPriorityBadge(announcement.priority)}
                      {!announcement.active && (
                        <Badge variant="outline" className="text-slate-500">
                          <EyeOff size={12} className="mr-1" /> Hidden
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-500 mb-2">
                      {getEventName(announcement.event_id)} • {formatDateTime(announcement.created_at)}
                    </p>
                    <p className="text-slate-700 whitespace-pre-wrap">{announcement.message}</p>
                  </div>

                  {isAdmin() && (
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleToggle(announcement.id)}
                        title={announcement.active ? "Hide" : "Show"}
                      >
                        {announcement.active ? <EyeOff size={16} /> : <Eye size={16} />}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditDialog(announcement)}
                      >
                        <Pencil size={16} />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => handleDelete(announcement.id)}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
